import React, { memo } from 'react'
import DropTrailerIcon from 'transfix-icons/build/DropTrailerIcon'
import { MapMarker } from 'transfix-ui/components/MapMarker'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { STOP_TYPE } from 'types'
import { IShipmentStop } from 'types/graphqlTypes'
import { capitalize } from 'utils/capitalize'

export interface IStopsDividerProps {
  stops?: Pick<
    IShipmentStop,
    'id' | 'city' | 'stateCode' | 'stopSequence' | 'stopType' | 'dropTrailer'
  >[]
  dataTest?: string
}

const useStyles = makeStyles(theme => ({
  coolGray5Bg: {
    backgroundColor: brandColors.coolGray4,
  },
  hrLineSingle: {
    width: theme.spacing(6),
    display: 'inline-block',
    margin: 0,
  },
  hrLine: {
    width: theme.spacing(2),
    display: 'inline-block',
    margin: 0,
  },
  flex: {
    display: 'flex',
    alignItems: 'center',
  },
  stopCount: {
    display: 'flex',
    alignItems: 'center',
    padding: `0 ${theme.spacing(0.5)}`,
    backgroundColor: brandColors.coolGray4,
    color: brandColors.white,
    borderRadius: theme.shape.borderRadius,
  },
  stopsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  singleStop: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
  },
  stopLocation: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}))

const StopsDivider = memo<IStopsDividerProps>(({ stops = [], dataTest }) => {
  const stopsBetween = stops.length - 2
  const isMultiStop = stopsBetween > 0

  const classes = useStyles()

  return isMultiStop ? (
    <Tooltip
      type='light'
      position='bottom'
      effect='solid'
      trigger={
        <div className={classes.flex} data-test={dataTest}>
          <hr className={`${classes.hrLine} ${classes.coolGray5Bg}`} />
          <div className={classes.stopCount}>{stopsBetween}</div>
          <hr className={`${classes.hrLine} ${classes.coolGray5Bg}`} />
        </div>
      }>
      <div className={classes.stopsList} data-test='stops-tooltip-content'>
        {stops.map(stop => {
          const { city, dropTrailer, id, stateCode, stopSequence, stopType } = stop || {}
          const cityName = capitalize(city || '')

          return (
            <div key={id} className={classes.singleStop}>
              <MapMarker mini sequence={stopSequence} type={(stopType as unknown) as STOP_TYPE} />
              <div className={classes.stopLocation}>
                <div className='text-caption-semibold text-default'>
                  {cityName}, {stateCode}
                </div>
                {dropTrailer && <DropTrailerIcon size='medium' color='coolGray5' />}
              </div>
            </div>
          )
        })}
      </div>
    </Tooltip>
  ) : (
    <div className={classes.flex} data-test={dataTest}>
      <hr className={`${classes.hrLineSingle} ${classes.coolGray5Bg}`} />
    </div>
  )
})

export default StopsDivider
